import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ClientLoggerService } from './client-logger.service';

/**
 * HTTP-Interceptor für automatisches Logging von HTTP-Requests
 * Loggt langsame Requests und Fehler automatisch
 */
@Injectable()
export class LoggingInterceptor implements HttpInterceptor {
  // Threshold für langsame Requests in Millisekunden
  private readonly SLOW_REQUEST_THRESHOLD_MS = 3000; // 3 Sekunden

  // URLs die nicht geloggt werden sollen (z.B. Logging-Endpoint selbst)
  private readonly EXCLUDED_URLS = [
    '/ClientLogging/LogEvent',
    '/ClientLogging/LogBatch'
  ];

  constructor(private logger: ClientLoggerService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Prüfe ob URL ausgeschlossen ist
    if (this.shouldExcludeUrl(req.url)) {
      return next.handle(req);
    }

    const startTime = Date.now();

    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          const duration = Date.now() - startTime;

          // Logge langsame Requests
          if (duration > this.SLOW_REQUEST_THRESHOLD_MS) {
            this.logger.logPerformance(
              `Slow HTTP Request: ${req.method} ${this.getShortUrl(req.url)}`,
              duration,
              {
                properties: {
                  method: req.method,
                  url: req.url,
                  statusCode: event.status,
                  slow: true
                }
              }
            );
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const duration = Date.now() - startTime;

        // Logge HTTP-Fehler
        this.logger.logHttpError(
          req.method,
          req.url,
          error.status,
          error.message || 'HTTP Error',
          duration
        );

        // Fehler weiterwerfen
        return throwError(() => error);
      })
    );
  }

  /**
   * Prüft ob URL vom Logging ausgeschlossen werden soll
   */
  private shouldExcludeUrl(url: string): boolean {
    return this.EXCLUDED_URLS.some(excluded => url.includes(excluded));
  }

  /**
   * Kürzt URL für bessere Lesbarkeit
   */
  private getShortUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      return url;
    }
  }
}
